import React, { useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useConversationFlow } from '@dev-streetbeat/chat';
import classNames from 'classnames';
import gsap from 'gsap';

import useThreadsStore from 'shared/store/threadsStore';

import styles from './portfolioSkeleton.module.scss';

type PortfolioSkeletonsProps = {
  show?: boolean;
};

export const PortfolioSkeleton: React.FC<PortfolioSkeletonsProps> = ({ show }) => {
  const { threadId } = useParams();
  const { isLoading } = useConversationFlow();
  const blockInput = useThreadsStore((state) => state.blockInput);
  const mainDivRef = useRef(null);
  const line1Ref = useRef(null);
  const line1CircleRef = useRef(null);
  const line2Ref = useRef(null);
  const line3Ref = useRef(null);
  const line4Ref = useRef(null);
  const animationRefs = useRef<GSAPTween[]>([]);

  useEffect(() => {
    if (line1Ref.current && line1CircleRef.current && line2Ref.current && line3Ref.current && line4Ref.current) {
      startAnimation1();
    }
    return () => {
      animationRefs.current.forEach((animation) => animation.kill());
    };
  }, []);
  const ease = 'back.out(1.7)';
  const easeCircle = 'back.out(2.7)';
  const easePower = 'power4.inOut';
  const startAnimation1 = () => {
    const anim = gsap.to(line1Ref.current, {
      width: 176,
      duration: 1,
      ease: ease,
      delay: 0.5,
      onStart: () => {
        startAnimationCircle();
        startAnimation2();
        startAnimation3();
        startAnimation4();
      },
    });
    animationRefs.current.push(anim);
  };
  const startAnimationCircle = () => {
    const anim = gsap.to(line1CircleRef.current, {
      width: 16,
      duration: 0.3,
      delay: 0.8,
      ease: easeCircle,
    });
    animationRefs.current.push(anim);
  };
  const startAnimation2 = () => {
    const anim = gsap.to(line2Ref.current, {
      width: 152,
      duration: 1,
      ease: ease,
      delay: 0.4,
    });
    animationRefs.current.push(anim);
  };
  const startAnimation3 = () => {
    const anim = gsap.to(line3Ref.current, {
      width: 128,
      duration: 1,
      delay: 0.5,
      ease: ease,
    });
    animationRefs.current.push(anim);
  };
  const startAnimation4 = () => {
    const anim = gsap.to(line4Ref.current, {
      width: 112,
      duration: 1,
      delay: 0.6,
      ease: ease,
      onComplete: () => {
        resetAnimation();
      },
    });
    animationRefs.current.push(anim);
  };
  const resetAnimation = () => {
    const anim = gsap.to(
      [line1Ref.current, line1CircleRef.current, line2Ref.current, line3Ref.current, line4Ref.current],
      {
        width: 0,
        duration: 0.6,
        delay: 1.2,
        ease: easePower,
        onComplete: () => {
          startAnimation1();
        },
      },
    );
    animationRefs.current.push(anim);
  };

  const shouldShow = show !== undefined ? show : isLoading || blockInput[threadId];

  return (
    <div ref={mainDivRef} className={styles.wrapper}>
      {shouldShow && (
        <>
          <div className={styles.firstLine}>
            <div ref={line1Ref} className={classNames(styles.line)}></div>
            <div ref={line1CircleRef} className={classNames(styles.lineCircle)}></div>
          </div>
          <div ref={line2Ref} className={classNames(styles.line)}></div>
          <div ref={line3Ref} className={classNames(styles.line)}></div>
          <div ref={line4Ref} className={classNames(styles.line)}></div>
        </>
      )}
    </div>
  );
};

export default PortfolioSkeleton;
